// Columna izquierda del explorer: Búsqueda · Nivel · Estado · Segmento · SCI · Créditos vigentes.
// Nada se ofrece sobre una lectura que no se hizo: sin la lista leída cae todo.
//
// Los filtros miran lo YA CARGADO, no el padrón: mientras queden páginas el panel lo declara
// arriba de todo (ESTANDAR-TABLAS §5.4).
import { Search, X } from 'lucide-react';
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import { Input } from '@/kit';
import { cn } from '@/lib/cn';
import {
    countActiveFilters,
    EMPTY_FILTERS,
    filtersAvailabilityOf,
    NO_LEVEL,
    toggleInList,
    type ActiveCreditsFilter,
    type CreditCustomersFilters,
    type LevelFilterValue,
} from '../lib/creditCustomersFilters';
import type { CustomerListState } from '../lib/creditCustomersErrors';
import {
    CUSTOMER_STATUS_LABEL,
    SCI_RANGE_FILTER_NOTE,
    SEGMENTS_UNREAD_NOTE,
    type CustomerCreditStatus,
} from '../lib/creditCustomersFormat';
import { TierPill } from '@/shared/patterns/Pills';

const LABEL = 'mb-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const GROUP = 'mt-2.5';
const FIELD = 'h-[26px] text-[11.5px]';
const SELECT =
    'h-[26px] w-full rounded-md border border-input bg-transparent px-2 text-[11.5px] outline-none focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50';

/** Los cuatro chips del mock. «No elegible» no es chip: se ve en la celda, no se filtra. */
const LEVEL_CHIPS: LevelFilterValue[] = [
    ClientLevelEnum.GOLD,
    ClientLevelEnum.SILVER,
    ClientLevelEnum.BRONZE,
    NO_LEVEL,
];

const STATUS_OPTIONS: { value: CustomerCreditStatus | ''; label: string }[] = [
    { value: '', label: 'Todos' },
    { value: 'CURRENT', label: CUSTOMER_STATUS_LABEL.CURRENT },
    { value: 'IN_ARREARS', label: CUSTOMER_STATUS_LABEL.IN_ARREARS },
    { value: 'NO_CREDITS', label: CUSTOMER_STATUS_LABEL.NO_CREDITS },
    { value: 'SETTLED', label: 'Liquidados' },
];

const ACTIVE_CREDITS_OPTIONS: { value: ActiveCreditsFilter; label: string }[] = [
    { value: '', label: 'Todos' },
    { value: '0', label: 'Ninguno' },
    { value: '1+', label: '1 o más' },
    { value: '2+', label: '2 o más' },
    { value: '3+', label: '3 o más' },
];

export function CreditCustomersFiltersPanel({
    filters,
    onChange,
    listState,
    collapsed,
    onToggleCollapsed,
    segmentOptions,
    segmentsRead,
    loadedNote,
}: {
    filters: CreditCustomersFilters;
    onChange: (next: CreditCustomersFilters) => void;
    /** Estado de la lista: sin ella no hay ni una fila que buscar, y cae también la búsqueda. */
    listState: CustomerListState;
    collapsed: boolean;
    onToggleCollapsed: () => void;
    /** Los segmentos que traen las filas CARGADAS, alfabéticos. No es el catálogo del tenant. */
    segmentOptions: { segmentId: string; name: string }[];
    /** El BFF pudo leer los segmentos de alguna fila. Sin ninguna, filtrar por segmento no afirma nada. */
    segmentsRead: boolean;
    /** Que el filtro mira lo cargado y no el padrón. `null` cuando ya no quedan páginas. */
    loadedNote: string | null;
}) {
    const patch = (partial: Partial<CreditCustomersFilters>) => {
        onChange({ ...filters, ...partial });
    };
    const { searchOff, creditOff, note, tone } = filtersAvailabilityOf(listState);
    const sciTitle = creditOff ? note : SCI_RANGE_FILTER_NOTE;
    const activeCount = countActiveFilters(filters);

    // Los segmentos vienen en la fila, pero su lectura falla APARTE en el BFF: sin ninguna leída no
    // hay pertenencia que afirmar.
    const segmentOff = searchOff || !segmentsRead;
    const segmentTitle = segmentOff ? (segmentsRead ? note : SEGMENTS_UNREAD_NOTE) : undefined;

    const clearAll = () => {
        onChange(EMPTY_FILTERS);
    };

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={activeCount}
            onReset={clearAll}
        >
            {loadedNote === null ? null : (
                <div
                    role="status"
                    className="mt-2 rounded border border-border bg-muted/40 px-2 py-1.5 text-[10px] leading-snug text-muted-foreground"
                >
                    {loadedNote}
                </div>
            )}

            {note ? (
                <div
                    role="status"
                    className={cn(
                        'mt-2 rounded border px-2 py-1.5 text-[10px] leading-snug',
                        tone === 'warning'
                            ? 'border-warning/40 bg-warning-soft text-warning'
                            : 'border-border bg-muted/40 text-muted-foreground',
                    )}
                >
                    {note}
                </div>
            ) : null}

            <div className={GROUP}>
                <div className={LABEL}>Búsqueda</div>
                <div className="relative">
                    <Search
                        className="pointer-events-none absolute left-1.5 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                        aria-hidden
                    />
                    <Input
                        type="search"
                        value={filters.query}
                        disabled={searchOff}
                        title={searchOff ? note : undefined}
                        onChange={(event) => {
                            patch({ query: event.target.value });
                        }}
                        placeholder="Nombre, teléfono, ID"
                        aria-label="Buscar cliente por nombre, teléfono o ID"
                        className={cn(FIELD, 'pl-6 pr-6')}
                    />
                    {filters.query !== '' ? (
                        <button
                            type="button"
                            onClick={() => {
                                patch({ query: '' });
                            }}
                            title="Limpiar búsqueda"
                            aria-label="Limpiar búsqueda"
                            className="absolute right-1 top-1/2 flex size-4 -translate-y-1/2 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                        >
                            <X className="size-3" aria-hidden />
                        </button>
                    ) : null}
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Nivel</div>
                <div className="flex flex-wrap gap-[3px]">
                    {LEVEL_CHIPS.map((value) => (
                        <TierPill
                            key={value}
                            level={value === NO_LEVEL ? null : value}
                            selected={filters.levels.includes(value)}
                            disabled={creditOff}
                            title={note}
                            onToggle={() => {
                                patch({
                                    levels: toggleInList<LevelFilterValue>(filters.levels, value),
                                });
                            }}
                        />
                    ))}
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Estado</div>
                <select
                    value={filters.status}
                    disabled={creditOff}
                    title={note}
                    aria-label="Estado del cliente"
                    onChange={(event) => {
                        patch({ status: event.target.value as CustomerCreditStatus | '' });
                    }}
                    className={SELECT}
                >
                    {STATUS_OPTIONS.map((option) => (
                        <option key={option.label} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Segmento</div>
                <select
                    value={filters.segmentId}
                    disabled={segmentOff}
                    title={segmentTitle}
                    aria-label="Segmento del cliente"
                    onChange={(event) => {
                        patch({ segmentId: event.target.value });
                    }}
                    className={SELECT}
                >
                    <option value="">Todos</option>
                    {segmentOptions.map((option) => (
                        <option key={option.segmentId} value={option.segmentId}>
                            {option.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>SCI</div>
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-1">
                    <Input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={100}
                        value={filters.sciMin}
                        disabled={creditOff}
                        title={sciTitle}
                        onChange={(event) => {
                            patch({ sciMin: event.target.value });
                        }}
                        placeholder="mín"
                        aria-label="SCI mínimo"
                        className={cn(FIELD, 'text-center tabular-nums')}
                    />
                    <span className="text-[10px] text-muted-foreground">—</span>
                    <Input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={100}
                        value={filters.sciMax}
                        disabled={creditOff}
                        title={sciTitle}
                        onChange={(event) => {
                            patch({ sciMax: event.target.value });
                        }}
                        placeholder="máx"
                        aria-label="SCI máximo"
                        className={cn(FIELD, 'text-center tabular-nums')}
                    />
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Créditos vigentes</div>
                <select
                    value={filters.activeCredits}
                    disabled={creditOff}
                    title={note}
                    aria-label="Créditos vigentes del acreditado"
                    onChange={(event) => {
                        patch({ activeCredits: event.target.value as ActiveCreditsFilter });
                    }}
                    className={SELECT}
                >
                    {ACTIVE_CREDITS_OPTIONS.map((option) => (
                        <option key={option.label} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
        </FiltersColumn>
    );
}
